import React, { useEffect, useState } from "react";
import "@/App.css";
import { Header } from "@/components/Header";
import { QuickCalculator } from "@/components/QuickCalculator";
import { NscaTable } from "@/components/NscaTable";
import { FormulaComparator } from "@/components/FormulaComparator";
import { CoachDashboard, useAthletes, DEFAULT_EXERCISES } from "@/components/CoachDashboard";
import { PlateLoader } from "@/components/PlateLoader";
import { RpeGrid } from "@/components/RpeGrid";
import { PrintSheet } from "@/components/PrintSheet";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import {
  buildShareUrl,
  readShareParams,
  copyToClipboard,
  isEmbed,
  startHeightSync,
} from "@/lib/share";
import { toNum } from "@/lib/calc";
import { exportChartPDF, exportCoachPDF } from "@/lib/pdf";
import {
  Dumbbell,
  Calculator,
  Users,
  ClipboardList,
  Gauge,
  Instagram,
} from "lucide-react";

const TABS = [
  { key: "calculator", label: "Calculateur", icon: Calculator },
  { key: "formulas", label: "Estimateur RM", icon: ClipboardList },
  { key: "rpe", label: "Grille RPE", icon: Gauge },
  { key: "coach", label: "Mode coach", icon: Users },
];

const SHARED = readShareParams();
const EMBED = isEmbed();

function App() {
  const [tab, setTab] = useState("calculator");
  const [unit, setUnit] = useState(SHARED?.unit || "lbs");
  const [oneRM, setOneRM] = useState(SHARED?.oneRM ?? 315);
  const [formula, setFormula] = useState("average");
  const [rounding, setRounding] = useState(
    SHARED?.rounding !== null && SHARED?.rounding !== undefined
      ? SHARED.rounding
      : 5,
  );
  const [exercise, setExercise] = useState(SHARED?.exercise || "Squat");
  const [selectedPct, setSelectedPct] = useState(SHARED?.pct || 85);
  const [plateCfg, setPlateCfg] = useState(
    SHARED?.unit === "kg"
      ? { profileKey: "ipf", bar: 20, collars: false }
      : { profileKey: "commercial", bar: 45, collars: false },
  );
  const [athletes, setAthletes] = useAthletes();

  const handleReset = () => {
    setOneRM(315);
    setExercise("Squat");
    setFormula("average");
    setRounding(5);
    setUnit("lbs");
    setSelectedPct(85);
  };

  const handleLoadIntoCalculator = (rm, ex) => {
    setOneRM(rm);
    setExercise(ex);
    setTab("calculator");
  };

  useEffect(() => {
    if (!EMBED) return undefined;
    return startHeightSync();
  }, []);

  const handleShare = async () => {
    const url = buildShareUrl({
      exercise,
      oneRM,
      unit,
      rounding,
      pct: selectedPct,
    });
    const ok = await copyToClipboard(url);
    toast[ok ? "success" : "error"](
      ok ? "Lien de la fiche copié" : "Copie impossible",
      {
        description: ok
          ? "Aucun nom d'athlète n'est inclus dans le lien."
          : "Copie le lien manuellement depuis la barre d'adresse.",
      },
    );
  };

  const handleExportPDF = () => {
    if (tab === "coach") {
      exportCoachPDF({ athletes, exercises: DEFAULT_EXERCISES, unit, rounding });
    } else {
      exportChartPDF({ oneRM, unit, rounding, exercise });
    }
  };

  return (
    <div className="App min-h-screen">
      <Toaster position="top-right" />
      <Header
        unit={unit}
        setUnit={setUnit}
        onPrint={() => window.print()}
        onReset={handleReset}
        onExportPDF={handleExportPDF}
        onShare={handleShare}
        embed={EMBED}
      />

      <PrintSheet
        exercise={exercise}
        oneRM={oneRM}
        unit={unit}
        rounding={rounding}
        selectedPct={selectedPct}
        plateCfg={plateCfg}
      />

      {/* Hero band */}
      {EMBED ? (
        <section className="no-print border-b border-white/10">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-4 flex flex-wrap gap-2">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  data-testid={`tab-${t.key}`}
                  onClick={() => setTab(t.key)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-sm text-xs font-mono uppercase tracking-widest border transition ${
                    active
                      ? "bg-[#cd383d] border-[#cd383d] text-white shadow-[0_0_18px_rgba(205,56,61,0.35)]"
                      : "border-white/10 text-slate-300 hover:text-white hover:border-white/30"
                  }`}
                >
                  <Icon size={14} /> {t.label}
                </button>
              );
            })}
          </div>
        </section>
      ) : (
      <section className="no-print relative border-b border-white/10 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10 sm:py-14 relative">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div className="max-w-2xl">
              <div className="eyebrow flex items-center gap-2">
                <Dumbbell size={12} /> Outil coach · Préparation physique
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-[0.95] mt-3 text-white">
                Calculateur <br className="hidden sm:block" />
                <span className="text-[#cd383d]">de charges</span>
              </h1>
              <div className="divider-red mt-5" />
              <p className="text-slate-400 text-sm sm:text-base mt-4 max-w-xl leading-relaxed">
                Table de charge / répétitions, estimation 1RM (Epley &
                Brzycki), projection multi-RM et matrice pour toute ton
                équipe. Bascule lbs/kg à la volée. Fiche PDF prête pour le
                plancher.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 lg:justify-end">
              {TABS.map((t) => {
                const Icon = t.icon;
                const active = tab === t.key;
                return (
                  <button
                    key={t.key}
                    data-testid={`tab-${t.key}`}
                    onClick={() => setTab(t.key)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-sm text-xs font-mono uppercase tracking-widest border transition ${
                      active
                        ? "bg-[#cd383d] border-[#cd383d] text-white shadow-[0_0_18px_rgba(205,56,61,0.35)]"
                        : "border-white/10 text-slate-300 hover:text-white hover:border-white/30"
                    }`}
                  >
                    <Icon size={14} /> {t.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      )}

      <main className="max-w-[1400px] mx-auto px-4 sm:px-8 py-8 sm:py-10">
        {tab === "calculator" && (
          <div className="grid grid-cols-12 gap-5 lg:gap-6" data-testid="view-calculator">
            <div className="col-span-12 lg:col-span-5 flex flex-col gap-5 lg:gap-6">
              <QuickCalculator
                unit={unit}
                oneRM={oneRM}
                setOneRM={setOneRM}
                formula={formula}
                setFormula={setFormula}
                rounding={rounding}
                setRounding={setRounding}
                exercise={exercise}
                setExercise={setExercise}
                exercises={DEFAULT_EXERCISES}
              />
              <PlateLoader
                weight={toNum(oneRM) * (selectedPct / 100)}
                unit={unit}
                label={`${selectedPct}% · ${exercise}`}
                cfg={plateCfg}
                setCfg={setPlateCfg}
              />
            </div>
            <div className="col-span-12 lg:col-span-7">
              <NscaTable
                oneRM={oneRM}
                unit={unit}
                rounding={rounding}
                exercise={exercise}
                selectedPct={selectedPct}
                onSelectPct={setSelectedPct}
              />
            </div>
          </div>
        )}

        {tab === "rpe" && (
          <div data-testid="view-rpe">
            <RpeGrid oneRM={oneRM} unit={unit} rounding={rounding} />
          </div>
        )}

        {tab === "formulas" && (
          <div data-testid="view-formulas">
            <FormulaComparator unit={unit} rounding={rounding} />
          </div>
        )}

        {tab === "coach" && (
          <div data-testid="view-coach">
            <CoachDashboard
              unit={unit}
              rounding={rounding}
              athletes={athletes}
              setAthletes={setAthletes}
              onLoadIntoCalculator={handleLoadIntoCalculator}
            />
          </div>
        )}
      </main>

      <section className="no-print border-t border-white/10 mt-10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-7 flex justify-center">
          <a
            href="https://www.instagram.com/kraken_p_p/"
            target="_blank"
            rel="noreferrer"
            data-testid="instagram-cta-button"
            className="btn-red px-5 py-3 rounded-sm text-sm flex items-center gap-2"
          >
            <Instagram size={18} /> S'abonner sur Instagram
          </a>
        </div>
      </section>

      {!EMBED && (
      <footer className="no-print border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">
            © Kraken Préparation Physique · Québec, QC
          </div>
          <a
            href="https://krakenpp.ca"
            target="_blank"
            rel="noreferrer"
            data-testid="footer-krakenpp-link"
            className="text-[11px] font-mono uppercase tracking-widest text-slate-400 hover:text-[#cd383d] transition"
          >
            krakenpp.ca →
          </a>
        </div>
      </footer>
      )}
    </div>
  );
}

export default App;
